// SourceList.jsx - Version corrigée
import React, { useState } from 'react';
import { Alert, Tooltip, CircularProgress } from '@mui/material';
import { 
  Save, Edit, Delete, OpenInNew, FilterList, Sort,
  InfoOutlined, Public, School, Business, Language, 
  Assessment, Add, Description, Sell,
  Warning, Error
} from '@mui/icons-material';

export default function SourceList({
  sources = [],
  metadata = {},
  filterType = '',
  sortField = 'relevanceScore',
  sortOrder = 'desc',
  page = 0,
  rowsPerPage = 10,
  saving = false,
  saveSuccess = false,
  saveError = null,
  onFilterChange,
  onSortChange,
  onChangePage,
  onChangeRowsPerPage,
  onEditSource,
  onDeleteSource,
  onShowSummary,
  onAddSource,
  onSaveSources
}) {
  // État pour l'expansion des résumés
  const [expandedSourceId, setExpandedSourceId] = useState(null);

  // Icônes par type de source
  const typeIcons = {
    Governmental: <Public className="h-5 w-5 text-blue-600" />,
    Educational: <School className="h-5 w-5 text-green-600" />,
    Commercial: <Business className="h-5 w-5 text-purple-600" />,
    'Non-profit': <Business className="h-5 w-5 text-yellow-600" />,
    Media: <Language className="h-5 w-5 text-red-600" />
  };
  
  // Icônes et couleurs pour les fournisseurs
  const providerStyles = {
    'OpenAI': { color: 'text-green-700 bg-green-50 border-green-200', icon: <Language className="h-4 w-4 mr-1" /> },
    'Google': { color: 'text-blue-700 bg-blue-50 border-blue-200', icon: <Public className="h-4 w-4 mr-1" /> },
    'Wikipedia': { color: 'text-gray-700 bg-gray-50 border-gray-200', icon: <School className="h-4 w-4 mr-1" /> },
    'Fallback': { color: 'text-amber-700 bg-amber-50 border-amber-200', icon: <Description className="h-4 w-4 mr-1" /> },
    'Manual': { color: 'text-purple-700 bg-purple-50 border-purple-200', icon: <Edit className="h-4 w-4 mr-1" /> },
    'Aladinn': { color: 'text-purple-700 bg-purple-50 border-purple-200', icon: <Language className="h-4 w-4 mr-1" /> }
  };
    
  // Fonction utilitaire pour obtenir le style du fournisseur
  const getProviderStyle = (provider) => {
    return providerStyles[provider] || { color: 'text-gray-700 bg-gray-50 border-gray-200', icon: <Sell className="h-4 w-4 mr-1" /> };
  };
  
  // Niveaux de fiabilité avec couleurs
  const reliabilityColors = {
    High: 'bg-green-100 text-green-800 border-green-200',
    Medium: 'bg-blue-100 text-blue-800 border-blue-200',
    Specialized: 'bg-purple-100 text-purple-800 border-purple-200'
  };

  // ✅ CORRECTION: Fonction pour déterminer si une source doit être affichée
  const shouldDisplaySource = (source) => {
    // Le backend renvoie seulement les sources qui ont une URL utilisable
    // Si une source est dans la liste, c'est qu'elle est accessible (URL complète ou domaine)
    
    // Ne pas afficher uniquement si complètement inaccessible ET sans fallback
    if (source.accessibilityStatus === 'completely_inaccessible' && 
        !source.usableUrl && !source.recommendedUrl && !source.fallbackApplied) {
      return false;
    }
    
    // Vérifier si c'est une page de parking détectée par le backend
    if (source.accessibilityInfo?.isParking || 
        (source.parkingIndicators && source.parkingIndicators.length > 0)) {
      return false;
    }
    
    // Respecter le filtrage basé sur le type de source si défini
    if (filterType && source.type && source.type !== filterType) {
      return false;
    }
    
    return true;
  };

  // ✅ CORRECTION: Fonction pour obtenir l'URL utilisable - TOUJOURS utiliser l'URL recommandée du backend
  const getUsableUrl = (source) => {
    // PRIORITÉ ABSOLUE: URL vérifiée et utilisable du backend
    if (source.usableUrl) {
      console.log(`Utilisation usableUrl pour ${source.title}: ${source.usableUrl}`);
      return source.usableUrl;
    }
    
    // PRIORITÉ 2: URL recommandée par le système d'accessibilité
    if (source.recommendedUrl) {
      console.log(`Utilisation recommendedUrl pour ${source.title}: ${source.recommendedUrl}`);
      return source.recommendedUrl;
    }
    
    // PRIORITÉ 3: Si fallback appliqué, utiliser le lien modifié
    if (source.fallbackApplied && source.link) {
      console.log(`Utilisation link avec fallback pour ${source.title}: ${source.link}`);
      return source.link;
    }
    
    // PRIORITÉ 4: URL/lien original seulement si pas de fallback
    if (source.link && source.accessibilityStatus === 'full_url_accessible') {
      console.log(`Utilisation link original pour ${source.title}: ${source.link}`);
      return source.link;
    }
    
    if (source.url && source.accessibilityStatus === 'full_url_accessible') {
      console.log(`Utilisation url original pour ${source.title}: ${source.url}`);
      return source.url;
    }
    
    // PRIORITÉ 5: Construire à partir du domaine vérifié
    if (source.verifiedDomain || source.domain) {
      const domain = source.verifiedDomain || source.domain;
      const domainUrl = domain.startsWith('http') ? domain : `https://${domain}`;
      console.log(`Utilisation domaine pour ${source.title}: ${domainUrl}`);
      return domainUrl;
    }
    
    console.warn(`Aucune URL utilisable trouvée pour ${source.title}`);
    return '#';
  };

  // ✅ CORRECTION: Fonction pour déterminer si un lien est accessible
  const isLinkAccessible = (source) => {
    // Si le backend a fourni une URL utilisable (complète ou domaine), le lien est accessible
    if (source.usableUrl || source.recommendedUrl) {
      return true;
    }
    
    // Vérifier les statuts d'accessibilité positifs
    const status = source.accessibilityStatus;
    if (status === 'full_url_accessible' || status === 'domain_fallback') {
      return true;
    }
    
    // Si fallback appliqué, c'est accessible
    if (source.fallbackApplied) {
      return true;
    }
    
    // Vérifier si c'est une page de parking (non accessible pour l'utilisateur)
    if (source.accessibilityInfo?.isParking || 
        (source.parkingIndicators && source.parkingIndicators.length > 0)) {
      return false;
    }
    
    // Si explicitement marqué comme inaccessible
    if (status === 'completely_inaccessible') {
      return false;
    }
    
    // Par défaut, si la source est dans la liste, elle devrait être accessible
    return true;
  };
  
  // Filtrer les sources à afficher (masquer les inaccessibles)
  const displayableSources = sources.filter(shouldDisplaySource);
  
  // Trier et paginer les sources
  const getSortedAndPaginatedSources = () => {
    // Appliquer le tri
    const sortedSources = [...displayableSources].sort((a, b) => {
      let valueA = a[sortField] || 0;
      let valueB = b[sortField] || 0;
      
      // Conversion si nécessaire
      if (typeof valueA === 'string' && !isNaN(valueA)) {
        valueA = parseFloat(valueA);
      }
      if (typeof valueB === 'string' && !isNaN(valueB)) {
        valueB = parseFloat(valueB);
      }
      
      // Pour les chaînes
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return sortOrder === 'asc' ? 
          valueA.localeCompare(valueB) : 
          valueB.localeCompare(valueA);
      }
      
      // Pour les nombres
      return sortOrder === 'asc' ? valueA - valueB : valueB - valueA;
    });
    
    // Appliquer la pagination
    return sortedSources.slice(
      page * rowsPerPage, 
      page * rowsPerPage + rowsPerPage
    );
  };

  const finalDisplaySources = getSortedAndPaginatedSources();
  
  return (
    <>
      {/* En-tête avec métadonnées et filtres */}
      <div className="space-y-4">
        {/* Informations sur la recherche */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <h3 className="text-xl font-bold text-gray-800">
                Sources recommandées
              </h3>
              
              <span className="flex items-center text-sm px-3 py-1 bg-blue-100 text-blue-700 rounded-full">
                <Assessment className="h-4 w-4 mr-1" />
                {displayableSources.length} sources
              </span>
              
              <span className="flex items-center text-sm px-3 py-1 bg-gray-100 text-gray-700 rounded-full">
                <InfoOutlined className="h-4 w-4 mr-1" />
                {metadata.category || 'Non spécifiée'}
              </span>

              {/* Afficher le nombre de sources filtrées si applicable */}
              {sources.length !== displayableSources.length && (
                <span className="flex items-center text-sm px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full">
                  <Warning className="h-4 w-4 mr-1" />
                  {sources.length - displayableSources.length} sources filtrées
                </span>
              )}
            </div>
            
            <div className="flex space-x-2">
              <Tooltip title="Ajouter une source manuellement">
                <button
                  onClick={onAddSource}
                  className="p-2 text-purple-600 hover:bg-purple-50 rounded-full transition-colors"
                >
                  <Add className="h-5 w-5" />
                </button>
              </Tooltip>
              
              <Tooltip title="Enregistrer toutes les sources">
                <button
                  onClick={onSaveSources}
                  disabled={saving || displayableSources.length === 0}
                  className={`p-2 rounded-full transition-colors ${
                    saving || displayableSources.length === 0
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-purple-600 hover:bg-purple-50'
                  }`}
                >
                  {saving ? <CircularProgress size={20} /> : <Save className="h-5 w-5" />}
                </button>
              </Tooltip>
            </div>
          </div>
          
          {/* Affichage des fournisseurs utilisés */}
          {metadata.providers && metadata.providers.length > 0 && (
            <div className="mt-3">
              <p className="text-sm text-gray-600 mb-2">
                Sources de données utilisées:
              </p>
              <div className="flex flex-wrap gap-1">
                {metadata.providers.map(provider => {
                  const style = getProviderStyle(provider);
                  return (
                    <span 
                      key={provider}
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${style.color}`}
                    >
                      {style.icon}
                      {provider}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        
        {/* Messages de sauvegarde */}
        {saveSuccess && (
          <Alert severity="success" className="rounded-lg">
            Sources sauvegardées avec succès!
          </Alert>
        )}
        
        {saveError && (
          <Alert severity="error" className="rounded-lg">
            {saveError}
          </Alert>
        )}
        
        {/* Filtres */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center text-gray-700 font-medium">
              <FilterList className="h-5 w-5 mr-1.5 text-purple-600" /> 
              <span>Filtrer par type:</span>
            </div>
            
            {['Governmental', 'Educational', 'Commercial', 'Non-profit', 'Media'].map(type => (
              <button
                key={type}
                onClick={() => onFilterChange(type)}
                className={`flex items-center px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                  filterType === type
                    ? 'bg-purple-100 border-purple-300 text-purple-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="mr-1.5">{typeIcons[type]}</span>
                {type}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Tableau des sources */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Source
                </th>
                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fournisseur
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors hover:bg-gray-100"
                  onClick={() => onSortChange('reliability')}
                >
                  <div className={`flex items-center justify-center ${sortField === 'reliability' ? 'text-purple-600' : 'text-gray-500'}`}>
                    <span className="mr-1">Fiabilité</span>
                    <Sort className={`h-4 w-4 ml-1 ${sortField === 'reliability' ? 'text-purple-600' : 'text-gray-400'}`} />
                    {sortField === 'reliability' && (
                      <span className="ml-1 text-sm font-bold">
                        {sortOrder === 'desc' ? '▼' : '▲'}
                      </span>
                    )}
                  </div>
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors hover:bg-gray-100"
                  onClick={() => onSortChange('relevanceScore')}
                >
                  <div className={`flex items-center justify-center ${sortField === 'relevanceScore' ? 'text-purple-600' : 'text-gray-500'}`}>
                    <span className="mr-1">Pertinence</span>
                    <Sort className={`h-4 w-4 ml-1 ${sortField === 'relevanceScore' ? 'text-purple-600' : 'text-gray-400'}`} />
                    {sortField === 'relevanceScore' && (
                      <span className="ml-1 text-sm font-bold">
                        {sortOrder === 'desc' ? '▼' : '▲'}
                      </span>
                    )}
                  </div>
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider cursor-pointer transition-colors hover:bg-gray-100"
                  onClick={() => onSortChange('popularityScore')}
                >
                  <div className={`flex items-center justify-center ${sortField === 'popularityScore' ? 'text-purple-600' : 'text-gray-500'}`}>
                    <span className="mr-1">Popularité</span>
                    <Sort className={`h-4 w-4 ml-1 ${sortField === 'popularityScore' ? 'text-purple-600' : 'text-gray-400'}`} />
                    {sortField === 'popularityScore' && (
                      <span className="ml-1 text-sm font-bold">
                        {sortOrder === 'desc' ? '▼' : '▲'}
                      </span>
                    )}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {/* Corps du tableau - Partie corrigée */}
              {finalDisplaySources.map((source, index) => (
                <tr key={source.id || source.domain || index} className="hover:bg-gray-50">
                  {/* COLONNE SOURCE SIMPLIFIÉE */}
                  <td className="px-6 py-4">
                    <div className="max-w-md">
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-sm font-medium text-gray-900">
                          {source.domain}
                        </div>
                        
                      </div>
                                            
                      {/* Résumé */}
                      {source.summary ? (
                        <div 
                          className="text-sm text-gray-700 mt-1"
                          onMouseEnter={() => setExpandedSourceId(source.id || source.domain)}
                          onMouseLeave={() => setExpandedSourceId(null)}
                        >
                          <div className={`bg-purple-50 p-2 rounded-md border border-purple-100 relative transition-all duration-300 ${
                            expandedSourceId === (source.id || source.domain) ? 'z-10 shadow-lg' : ''
                          }`}>
                            <div className={`${
                              expandedSourceId === (source.id || source.domain)
                              ? 'line-clamp-none max-h-80 overflow-y-auto'
                              : 'line-clamp-3'
                            } transition-all duration-300`}>
                              {typeof source.summary === 'object' 
                                ? (source.summary.summary || source.description || 'Aucun résumé disponible') 
                                : (source.summary || source.description || 'Aucun résumé disponible')}
                              
                              {source.summaryError && (
                                <span className="inline-block mt-1 text-xs text-amber-600 italic">
                                  (Résumé automatique)
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500 line-clamp-2">
                          {source.description || 'Aucune description disponible'}
                        </div>
                      )}
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      <span className="mr-1">{typeIcons[source.type] || <Public className="h-4 w-4" />}</span>
                      {source.type || 'Unknown'}
                    </span>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {source.provider && (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getProviderStyle(source.provider).color}`}>
                        {getProviderStyle(source.provider).icon}
                        {source.provider}
                      </span>
                    )}
                    {!source.provider && source.searchMethod && (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getProviderStyle(source.searchMethod).color}`}>
                        {getProviderStyle(source.searchMethod).icon}
                        {source.searchMethod}
                      </span>
                    )}
                    {!source.provider && !source.searchMethod && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border bg-gray-100 text-gray-800 border-gray-200">
                        <Sell className="h-4 w-4 mr-1" />
                        Inconnu
                      </span>
                    )}
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${reliabilityColors[source.reliability] || 'bg-gray-100 text-gray-800 border-gray-200'}`}>
                      {source.reliability || 'Medium'}
                    </span>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="flex items-center justify-center">
                      <div className={`w-16 h-2 rounded-full ${
                        (source.relevanceScore || 5) >= 8 ? 'bg-green-500' :
                        (source.relevanceScore || 5) >= 6 ? 'bg-blue-500' :
                        (source.relevanceScore || 5) >= 4 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}>
                        <div 
                          className="h-full bg-gray-200 rounded-full" 
                          style={{ width: `${100 - ((source.relevanceScore || 5) * 10)}%` }}
                        ></div>
                      </div>
                      <span className="ml-2 text-sm text-gray-700">
                        {source.relevanceScore || 5}/10
                      </span>
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="flex items-center justify-center">
                      <div className="w-16 h-2 rounded-full bg-purple-500">
                        <div 
                          className="h-full bg-gray-200 rounded-full" 
                          style={{ width: `${100 - ((source.popularityScore || 5) * 10)}%` }}
                        ></div>
                      </div>
                      <span className="ml-2 text-sm text-gray-700">
                        {source.popularityScore || 5}/10
                      </span>
                    </div>
                  </td>
                  
                  {/* COLONNE ACTIONS SIMPLIFIÉE */}
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <Tooltip title="Éditer">
                        <button
                          onClick={() => onEditSource(source)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                      </Tooltip>
                      
                      <Tooltip title="Supprimer">
                        <button
                          onClick={() => onDeleteSource(source)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Delete className="h-5 w-5" />
                        </button>
                      </Tooltip>
                      
                      {/* Lien utilisant TOUJOURS l'URL recommandée du backend */}
                      {isLinkAccessible(source) ? (
                        <Tooltip title={
                          source.accessibilityStatus === 'domain_fallback' || source.fallbackApplied
                            ? `Visiter ${source.verifiedDomain || source.domain || 'le site'} (page d'accueil disponible)` 
                            : source.userMessage?.action || "Visiter le site"
                        }>
                          <a
                            href={getUsableUrl(source)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-600 hover:text-gray-900"
                            onClick={() => {
                              console.log(`Clic sur lien pour ${source.title}: ${getUsableUrl(source)}`);
                            }}
                          >
                            <OpenInNew className="h-5 w-5" />
                          </a>
                        </Tooltip>
                      ) : (
                        <Tooltip title={
                          source.accessibilityInfo?.isParking 
                            ? "Page de parking/domaine à vendre" 
                            : source.parkingIndicators?.length > 0 
                              ? "Domaine détecté comme parking" 
                              : "Site complètement inaccessible"
                        }>
                          <span className="text-gray-400 cursor-not-allowed">
                            <OpenInNew className="h-5 w-5" />
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => onChangePage(page - 1)}
              disabled={page === 0}
              className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                page === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Précédent
            </button>
            <button
              onClick={() => onChangePage(page + 1)}
              disabled={page >= Math.ceil(displayableSources.length / rowsPerPage) - 1}
              className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                page >= Math.ceil(displayableSources.length / rowsPerPage) - 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Suivant
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Affichage de <span className="font-medium">{page * rowsPerPage + 1}</span> à{' '}
                <span className="font-medium">
                  {Math.min((page + 1) * rowsPerPage, displayableSources.length)}
                </span>{' '}
                sur <span className="font-medium">{displayableSources.length}</span> sources accessibles
                {sources.length !== displayableSources.length && (
                  <span className="text-yellow-600 ml-1">
                    ({sources.length - displayableSources.length} filtrées)
                  </span>
                )}
              </p>
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">Lignes par page:</span>
                <select
                  className="border-gray-300 rounded-md text-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                  value={rowsPerPage}
                  onChange={(e) => onChangeRowsPerPage(parseInt(e.target.value, 10))}
                >
                  {[5, 10, 25, 50].map(value => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
                
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => onChangePage(page - 1)}
                    disabled={page === 0}
                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium ${
                      page === 0
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    <span className="sr-only">Précédent</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  
                  {/* Pages */}
                  {[...Array(Math.min(5, Math.ceil(displayableSources.length / rowsPerPage)))].map((_, i) => {
                    const pageNumber = page <= 2 
                      ? i 
                      : page >= Math.ceil(displayableSources.length / rowsPerPage) - 3 
                        ? Math.ceil(displayableSources.length / rowsPerPage) - 5 + i 
                        : page - 2 + i;
                    
                    if (pageNumber >= 0 && pageNumber < Math.ceil(displayableSources.length / rowsPerPage)) {
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => onChangePage(pageNumber)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === pageNumber
                              ? 'z-10 bg-purple-50 border-purple-500 text-purple-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNumber + 1}
                        </button>
                      );
                    }
                    return null;
                  })}
                  
                  <button
                    onClick={() => onChangePage(page + 1)}
                    disabled={page >= Math.ceil(displayableSources.length / rowsPerPage) - 1}
                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium ${
                      page >= Math.ceil(displayableSources.length / rowsPerPage) - 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    <span className="sr-only">Suivant</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Bouton de sauvegarde en bas */}
      <div className="flex justify-end mt-6">
        <button
          onClick={onSaveSources}
          disabled={saving || displayableSources.length === 0}
          className={`inline-flex items-center px-6 py-3 rounded-lg text-white font-medium transition-all duration-200 ${
            saving || displayableSources.length === 0
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-purple-600 hover:bg-purple-700 shadow-md hover:shadow-lg transform hover:-translate-y-0.5'
          }`}
        >
          {saving ? (
            <>
              <CircularProgress size={20} className="mr-2" />
              Sauvegarde en cours...
            </>
          ) : (
            <>
              <Save className="h-5 w-5 mr-2" />
              Enregistrer les sources ({displayableSources.length})
            </>
          )}
        </button>
      </div>

      {/* Message si aucune source n'est affichable */}
      {displayableSources.length === 0 && sources.length > 0 && (
        <div className="text-center py-12">
          <Error className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Aucune source accessible trouvée
          </h3>
          <p className="text-gray-600 mb-4">
            Toutes les sources trouvées étaient inaccessibles et ont été automatiquement filtrées.
          </p>
          <p className="text-sm text-gray-500">
            Essayez de modifier votre recherche ou d'utiliser des mots-clés différents.
          </p>
        </div>
      )}
    </>
  );
} 